import { Component, computed, OnDestroy, OnInit, signal } from '@angular/core';
import { Subject, throwError } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';

import { AdminService } from '../services/admin.service';
import { AdminOverviewResult } from '../models/admin-overview.model';
import { NotificationService } from '../../../core/services';

/** `.toISOString()` always carries milliseconds; trimmed to match the
 *  backend's own examples (`...23:59:59Z`, not `...23:59:59.999Z`). */
function toIsoSeconds(d: Date): string {
  return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

@Component({
  selector: 'app-admin-overview',
  standalone: false,
  templateUrl: './admin-overview.html',
  styleUrl: './admin-overview.scss',
})
export class AdminOverview implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();

  overview: AdminOverviewResult | null = null;
  isLoading = false;

  readonly rangeOptions = [
    { days: 7, label: 'Last 7 days' },
    { days: 30, label: 'Last 30 days' },
    { days: 90, label: 'Last 90 days' },
    { days: 365, label: 'Last year' },
  ];
  readonly selectedRangeDays = signal(30);
  rangeMenuOpen = false;

  constructor(
    private adminService: AdminService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.load(this.selectedRangeDays());
  }

  /** One call: the backend embeds `previous` (the comparison window) in
   *  the response itself, same as GET /usage's `deltas`. */
  private load(days: number): void {
    const { from, to } = this.computeRange(days);

    this.isLoading = true;
    this.adminService.getOverview({ from, to })
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          this.notificationService.error('Failed to load the platform overview. Please try again later.');
          return throwError(() => error);
        }),
        tap(current => { this.overview = current; }),
        finalize(() => { this.isLoading = false; }),
      ).subscribe();
  }

  /** `days` whole UTC calendar days ending on `end`'s date (default today),
   *  snapped to day boundaries (00:00:00Z / 23:59:59Z) per the backend's
   *  contract note on covering whole days. */
  private computeRange(days: number, end = new Date()): { from: string; to: string } {
    const to = new Date(end);
    to.setUTCHours(23, 59, 59, 999);

    const from = new Date(end);
    from.setUTCDate(from.getUTCDate() - (days - 1));
    from.setUTCHours(0, 0, 0, 0);

    return { from: toIsoSeconds(from), to: toIsoSeconds(to) };
  }

  selectRange(days: number): void {
    this.selectedRangeDays.set(days);
    this.rangeMenuOpen = false;
    this.load(days);
  }

  toggleRangeMenu(event: MouseEvent): void {
    event.stopPropagation();
    const wasOpen = this.rangeMenuOpen;
    this.closeMenu();
    if (!wasOpen) {
      this.rangeMenuOpen = true;
    }
  }

  /** Bound to (appClickOutside) on the range dropdown wrapper in the
   *  template safe to call even when nothing's open. */
  closeMenu(): void {
    this.rangeMenuOpen = false;
  }

  /** "Last 30 days" etc., matching whichever rangeOptions entry is selected
   *  same convention as Home (see home.ts), showing the picked option's
   *  own label instead of a computed date range. A computed signal instead
   *  of a getter: a getter re-runs on every change-detection pass (e.g. a
   *  mousemove), a computed only re-runs when selectedRangeDays changes. */
  readonly rangeLabel = computed(() => this.rangeOptions.find(opt => opt.days === this.selectedRangeDays())?.label ?? '');

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
